import { useMemo, useEffect } from "react";
import { useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { CustomTooltip } from "../../components/ReportDetails/ReportData/BDN/BDNConstants";

const rowColors = {
	ERROR: "#f66764",
	WARNING: "#fada6c",
	EXCLUSION: "#8ec7fd",
};

const getValidationBg = (validationType) => {
	if (rowColors[validationType]) {
		return rowColors[validationType]; // Return the color based on validation type
	}
	return "white"; // Default color if no match found
};

/**
 * This custom hook is used to create a table with Material React Table.
 * It uses the useMaterialReactTable hook to create a table settings with the provided data and columns.
 * It also handles row selection and actions for editing and deleting rows.
 * @param {Object} props - The properties for the table.
 * @param {Array} props.columns - The columns definition for the table.
 * @param {Array} props.data - The data to be displayed in the table.
 * @param {Function} props.onEdit - Callback function to handle row edit action.
 * @param {Function} props.onDelete - Callback function to handle row delete action.
 * @param {boolean} props.actionsDisabled - Flag to disable actions in the table. 
 * @param {*} params 
 * @returns 
 */
const useDetailsTable = ({ columns, data, onEdit, onDelete, actionsDisabled }) => {
	const onClickRow = (row, table) => {
		if (actionsDisabled) return; // Prevent action if actions are disabled
		onEdit(row, table);
	};

	const onDeleteRow = (row, table) => {
		if (actionsDisabled) return; // Prevent action if actions are disabled
		onDelete(row, table);
	};

	const enableActions = !actionsDisabled; // Check if actions are enabled

	return useMaterialReactTable({
		columns: columns,
		data: data,
		enableRowSelection: enableActions,
		enableStickyHeader: true,
		enableDensityToggle: false,
		enablePagination: false,
		enableBottomToolbar: false,
		enableFullScreenToggle: false,
		enableColumnFilters: false,
		enableGlobalFilter: false,

		muiTableBodyRowProps: ({ row }) => {
			const rowValues = row.original; // Get the original row data
			const backgroundColor = getValidationBg(rowValues?._validateType); // Set background color based on validation type
			return {
				sx: { backgroundColor: `${backgroundColor}` },
			};
		},

		enableEditing: enableActions,
		initialState: {
			columnPinning: { left: ["mrt-row-select", "mrt-row-actions"] },
		},

		displayColumnDefOptions: {
			"mrt-row-actions": {
				header: "",
				size: 60,
				muiTableBodyCellProps: {
					className: "mrt-row-actions-style",
				},
			},
			"mrt-row-select": {
				size: 60,
			},
		},
		muiTableFooterProps: {
			sx: {
				"& tr": {
					backgroundColor: "#f9f9f9 !important",
				},
			},
		},

		renderRowActions: ({ row, table }) => (
			<Box sx={{ display: "flex", gap: "1rem" }}>
				{onEdit && (
					<CustomTooltip title={row?.original?._validationMessage || "Edit"}>
						<IconButton onClick={() => onClickRow(row, table)}>
							<EditIcon />
						</IconButton>
					</CustomTooltip>
				)}
				{onDelete && (
					<CustomTooltip title={row?.original?._validationMessage || "Delete"}>
						<IconButton color="error" onClick={() => onDeleteRow(row, table)}>
							<DeleteIcon />
						</IconButton>
					</CustomTooltip>
				)}
			</Box>
		),
	});
};

/**
 * 
 * Custom hook to create a table with aggregation functionality using Material React Table.
 * It uses the useMaterialReactTable hook to create a table with the provided data and columns.
 * It also handles row selection and aggregation for specific columns.
 * @param {Object} props - The properties for the table.
 * @param {Array} props.data - The data to be displayed in the table.
 * @param {Array} props.columnsDefinition - The definition of the columns in the table.
 * @param {Function} props.onEdit - Callback function to handle row edit action.
 * @param {Function} props.onDelete - Callback function to handle row delete action.
 * @param {Function} props.onRowSelection - Callback function to handle row selection changes.
 * @param {Object} props.options - Additional options for the table.
 * @param {boolean} props.options.actionsDisabled - Flag to disable actions in the table. *  
 * @returns {Object} - The table settings object containing the table instance and other properties. 
 */
export const useTableWithAggregation = ({ data: tableData, columnsDefinition, onEdit, onDelete, onRowSelection, options }) => {
	const data = useMemo(() => tableData, [tableData]);
	const columns = useMemo(() => columnsDefinition, [columnsDefinition]);
	const { actionsDisabled } = options || {};

	columnsDefinition
		.filter((x) => x.aggregationFn)
		.forEach((column) => {
			column.Footer = ColumnFooter;
		});
	const tableSettings = useDetailsTable({ columns: columns, data: data, onEdit: onEdit, onDelete: onDelete, actionsDisabled });

	const rowSelection = tableSettings.getState().rowSelection;
	// Monitoring row selection changes
	// if selected rows change, call the parent onRowSelection function with the selected row IDs
	useEffect(() => {
		if (onRowSelection) {
			const selectedRowsId = Object.keys(rowSelection).filter((key) => rowSelection[key]);
			onRowSelection(selectedRowsId);
		}
	}, [rowSelection]);

	return { tableSettings };
};

useTableWithAggregation.propTypes = {
	data: PropTypes.object.isRequired,
	columnsDefinition: PropTypes.array.isRequired, // Added prop validation for columnsDefinition
	onEdit: PropTypes.func,
	onDelete: PropTypes.func,
	onRowSelection: PropTypes.func, // Added prop validation for onRowSelection
	options: PropTypes.shape({
		actionsDisabled: PropTypes.bool, // Add validation for actionsDisabled inside options
	}),
};

/**
 * This basic component is used to render the footer of a column in a table. 
 * @param {*} params
 * @returns 
 */

const CustomFooter = ({ value }) => (
	<Box color="black" fontSize={"14px"}>
		{value}
	</Box>
);
CustomFooter.propTypes = {
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

// Function to calculate the sum of numbers in an array
// This function is used to calculate the total for the column footer
const sumNumbers = (arr) =>
	arr.reduce((sum, current) => {
		if (!isNaN(current)) {
			return sum + parseFloat(current || 0); // Convert to number and add to sum
		}
		return sum;
	}, 0);


/**
 * This component is used to render the footer of a column in a table.
 * It calculates the total of the column values based on the aggregation function defined in the column definition.
 * @param {*} params
 * @param {Object} params.column - The column object containing the column definition and data.
 * @param {Object} params.table - The table object containing the table instance and data. *  
 * @returns 
 */
const ColumnFooter = ({ column, table }) => {
	const columnId = column.id;

	// use getRowModel instead of getFilteredRowModel to get the visible rows
	const allDataRows = table?.getFilteredRowModel().rows.map((row) => row.original); // Get all data rows

	// Get the values of the column for all data rows
	const columnValues = allDataRows.map((row) => row[columnId]);

	// Check if the column has an aggregation function defined
	const aggregationType = column.columnDef?.aggregationFn;

	// return the calculated value for the column footer
	// If no aggregation function is defined, return null
	const total = aggregationFn(aggregationType, columnValues); // Calculate the sum of the column values
	return <CustomFooter value={total?.toFixed(2)} />;
};

// Function to determine the aggregation type and calculate the total
// This function is used to calculate the total for the column footer
const aggregationFn = (type, columnValues) => {
	if (type === "sum") {
		return sumNumbers(columnValues);
	}
	return "";
};

ColumnFooter.propTypes = {
	column: PropTypes.object.isRequired,
	table: PropTypes.object.isRequired,
};
